import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useRealtimeEvent } from "../contexts/RealtimeContext";
import { Layout } from "../components/Layout";
import NotificationPrefsCard from "../components/NotificationPrefsCard";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Bell, BellRing, Heart, Copy, LogOut, UserMinus, Sparkles, Link as LinkIcon, Share2, Smartphone, Plus } from "lucide-react";

// Helper: convert URL-safe base64 to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

// Detect iOS (incl. iPadOS, which masquerades as Mac with touch)
const isIOS = (() => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  // iPadOS 13+ reports MacIntel — disambiguate via touch
  return navigator.platform === "MacIntel" && (navigator.maxTouchPoints || 0) > 1;
})();

// Detect standalone (PWA installed) — iOS Safari uses navigator.standalone
const isStandalone = (() => {
  if (typeof window === "undefined") return false;
  if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
  return !!window.navigator.standalone;
})();

export default function Settings() {
  const { user, couple, partner, logout, refresh } = useAuth();
  const [code, setCode] = useState("");
  const [pushStatus, setPushStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [magicLink, setMagicLink] = useState("");
  const [magicLoading, setMagicLoading] = useState(false);

  // When partner joins via invite code, refresh couple state automatically
  useRealtimeEvent(["couple:joined"], () => {
    refresh();
    toast.success("Partner:in ist verbunden 💛");
  });

  const copyCode = () => {
    if (couple?.invite_code) {
      navigator.clipboard.writeText(couple.invite_code);
      toast.success("Code kopiert");
    }
  };

  const generateMagicLink = async () => {
    setMagicLoading(true);
    try {
      const { data } = await api.post("/couple/invite-link");
      setMagicLink(`${window.location.origin}/join/${data.token}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setMagicLoading(false);
    }
  };

  const copyMagicLink = () => {
    if (!magicLink) return;
    navigator.clipboard.writeText(magicLink);
    toast.success("Einladungs-Link kopiert");
  };

  const shareMagicLink = async () => {
    if (!magicLink) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Wisha — Couple Space",
          text: `${user?.name || "Jemand"} möchte dich auf Wisha verbinden 💛`,
          url: magicLink,
        });
      } catch {
        /* user cancelled */
      }
    } else {
      copyMagicLink();
    }
  };

  const createCouple = async () => {
    setLoading(true);
    try {
      await api.post("/couple/create");
      await refresh();
      toast.success("Couple Space erstellt");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const joinCouple = async () => {
    setLoading(true);
    try {
      await api.post("/couple/join", { invite_code: code });
      await refresh();
      toast.success("Couple Space verbunden 💛");
      setCode("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const leaveCouple = async () => {
    if (!window.confirm("Wirklich aus dem Couple Space austreten?")) return;
    try {
      await api.post("/couple/leave");
      await refresh();
      toast.success("Du hast den Space verlassen");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const enablePush = async () => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      // iOS Safari only exposes PushManager on standalone PWAs.
      if (isIOS && !isStandalone) {
        toast.error('Bitte zuerst „Zum Home-Bildschirm hinzufügen" und Wisha von dort aus öffnen.');
      } else {
        toast.error("Push wird in diesem Browser nicht unterstützt");
      }
      return;
    }
    try {
      const reg = await navigator.serviceWorker.register("/sw.js");
      await navigator.serviceWorker.ready;
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        toast.error("Push-Berechtigung verweigert");
        return;
      }
      const { data: vapid } = await api.get("/push/vapid-key");
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapid.public_key),
      });
      const subJson = sub.toJSON();
      await api.post("/push/subscribe", {
        endpoint: subJson.endpoint,
        keys: subJson.keys,
      });
      toast.success("Push aktiviert 🔔");
      setPushStatus("active");
    } catch (err) {
      toast.error("Push fehlgeschlagen: " + (err.message || ""));
    }
  };

  const sendTest = async () => {
    try {
      await api.post("/push/test");
      toast.success("Test-Notification gesendet");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <Layout>
      <header className="mb-12">
        <div className="text-[10px] uppercase tracking-[0.3em] text-text_secondary mb-3">Einstellungen</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-ink leading-tight tracking-tight">Euer Profil & Setup</h1>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-4xl">
        {/* Profile */}
        <div className="bg-white border border-border rounded-3xl p-8">
          <h2 className="font-serif text-2xl text-ink mb-1">Profil</h2>
          <p className="text-text_secondary text-sm mb-6">Deine Account-Infos.</p>
          <div className="space-y-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-text_secondary">Name</div>
              <div className="text-ink mt-1">{user?.name}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-text_secondary">Email</div>
              <div className="text-ink mt-1">{user?.email}</div>
            </div>
          </div>
          <Button onClick={logout} variant="outline" className="rounded-full mt-8" data-testid="settings-logout-button">
            <LogOut size={14} className="mr-2" /> Abmelden
          </Button>
        </div>

        {/* Couple */}
        <div className="bg-white border border-border rounded-3xl p-8">
          <h2 className="font-serif text-2xl text-ink mb-1">Couple Space</h2>
          <p className="text-text_secondary text-sm mb-6">Verbindet euch zu zweit für gemeinsame Listen.</p>

          {couple ? (
            <>
              {partner ? (
                <div className="rounded-2xl bg-bone p-5 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-terracotta text-white flex items-center justify-center font-medium" data-testid="settings-partner-avatar">
                    {partner.name?.charAt(0)?.toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-ink" data-testid="settings-partner-name">{partner.name}</div>
                    <div className="text-xs text-text_secondary">verbunden</div>
                  </div>
                  <Heart size={18} className="text-terracotta" />
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="rounded-2xl bg-bone p-5">
                    <div className="text-sm text-ink mb-3">Lade deine Partner:in mit diesem Code ein:</div>
                    <div className="flex items-center gap-3">
                      <div className="font-mono text-2xl tracking-[0.4em] text-ink" data-testid="settings-invite-code">{couple.invite_code}</div>
                      <button onClick={copyCode} className="p-2 rounded-full bg-white hover:bg-alabaster" data-testid="settings-copy-code"><Copy size={14} /></button>
                    </div>
                  </div>
                  <div className="rounded-2xl border border-dashed border-border p-5">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-text_secondary mb-3">
                      <LinkIcon size={12} /> Magic-Link
                    </div>
                    {!magicLink ? (
                      <Button
                        onClick={generateMagicLink}
                        disabled={magicLoading}
                        variant="outline"
                        className="rounded-full"
                        data-testid="settings-generate-magic-link"
                      >
                        {magicLoading ? "Generiere …" : "Magic-Link erstellen"}
                      </Button>
                    ) : (
                      <div className="space-y-3" data-testid="settings-magic-link-section">
                        <div className="bg-bone rounded-xl px-3 py-2">
                          <span className="font-mono text-xs text-text_secondary truncate block" data-testid="settings-magic-link-url">{magicLink}</span>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          <Button onClick={copyMagicLink} variant="outline" size="sm" className="rounded-full" data-testid="settings-copy-magic-link">
                            <Copy size={12} className="mr-1.5" /> Kopieren
                          </Button>
                          <Button onClick={shareMagicLink} size="sm" className="rounded-full bg-ink text-alabaster hover:bg-ink/90" data-testid="settings-share-magic-link">
                            <Share2 size={12} className="mr-1.5" /> Teilen
                          </Button>
                        </div>
                        <p className="text-[11px] text-text_secondary">14 Tage gültig</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
              <Button onClick={leaveCouple} variant="ghost" className="rounded-full mt-6 text-terracotta hover:text-terracotta hover:bg-[#F5E2DA]" data-testid="settings-leave-couple">
                <UserMinus size={14} className="mr-2" /> Couple Space verlassen
              </Button>
            </>
          ) : (
            <div className="space-y-6">
              <Button onClick={createCouple} disabled={loading} className="w-full rounded-full bg-ink text-alabaster hover:bg-ink/90 py-6" data-testid="settings-create-couple">
                <Sparkles size={14} className="mr-2" /> Couple Space erstellen
              </Button>
              <div className="text-center text-text_secondary text-xs uppercase tracking-[0.2em]">oder</div>
              <div className="space-y-3">
                <div className="text-[10px] uppercase tracking-[0.2em] text-text_secondary">Mit Code beitreten</div>
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="A1B2C3"
                  maxLength={6}
                  className="rounded-full bg-bone border-border text-center font-mono tracking-[0.3em]"
                  data-testid="settings-invite-code-input"
                />
                <Button onClick={joinCouple} disabled={loading || code.length !== 6} variant="outline" className="w-full rounded-full" data-testid="settings-join-couple">
                  Beitreten
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Push Notifications */}
        <div className="bg-white border border-border rounded-3xl p-8 lg:col-span-2">
          <h2 className="font-serif text-2xl text-ink mb-1">Erinnerungen</h2>
          <p className="text-text_secondary text-sm mb-6">Aktiviere Push-Benachrichtigungen für wiederkehrende Ziele, Jahrestage und neue Wünsche.</p>

          {isIOS && !isStandalone ? (
            <div className="rounded-2xl bg-bone p-5 space-y-3" data-testid="settings-ios-pwa-hint">
              <div className="flex items-center gap-2 text-ink">
                <Smartphone size={16} />
                <span className="font-medium text-sm">iOS: Erst zur Home-Ansicht hinzufügen</span>
              </div>
              <p className="text-sm text-text_secondary leading-relaxed">
                Push-Benachrichtigungen funktionieren auf iPhone &amp; iPad nur, wenn Wisha als App installiert ist. So geht's:
              </p>
              <ol className="text-sm text-text_secondary space-y-1.5 list-decimal pl-5">
                <li>Tippe unten in Safari auf das <Share2 size={12} className="inline -mt-0.5" />-Teilen-Icon</li>
                <li>Wähle „Zum Home-Bildschirm" <Plus size={12} className="inline -mt-0.5" /></li>
                <li>Öffne Wisha vom Home-Bildschirm aus</li>
                <li>Komm hierher zurück und aktiviere Push</li>
              </ol>
              <p className="text-[11px] text-text_secondary pt-1">Voraussetzung: iOS 16.4 oder neuer.</p>
            </div>
          ) : (
            <div className="flex flex-wrap gap-3">
              <Button onClick={enablePush} className="rounded-full bg-ink text-alabaster hover:bg-ink/90" data-testid="settings-enable-push">
                <Bell size={14} className="mr-2" /> Push aktivieren
              </Button>
              <Button onClick={sendTest} variant="outline" className="rounded-full" data-testid="settings-test-push">
                <BellRing size={14} className="mr-2" /> Test senden
              </Button>
            </div>
          )}
        </div>

        <NotificationPrefsCard />
      </div>
    </Layout>
  );
}
